import { useEffect, useMemo, useRef, useState } from 'react';
import {
  IBusiness,
  IContact,
  IContactField,
  IContactTag,
} from '@trii/types/dist/Contacts/contacts';
import { ILabel } from '@trii/types/dist/Contacts';
import { createEditContactApi } from '../services/api';
import { isBusiness } from '../types/is-business.typeguard';
import { isContact } from '../types/is-contact.typeguard';
import { UserTrii } from '@trii/types/dist/Users';
import { Spaces } from '@trii/types';
import formatContactName from '../../../utils/formatContactName';

export type UseEditContactModalControllerArgs = {
  open: boolean;
  baseUrl?: string;
  spaceId?: string;
  contactId?: string;
  entityId?: string;
  entityType?: 'contact' | 'business';
  initialContactData?: IContact | IBusiness;
  initialEntityData?: IContact | IBusiness;
  contactNameFormat?: Spaces.nameFormat;
  resetOnClose?: boolean;
};

type ControllerState = {
  isLoading: boolean;
  error: unknown;
  contactData: IContact | IBusiness | null;
  labels: ILabel[];
  labelsLoading: boolean;
  labelsError: unknown;
  contactFields: IContactField[];
  contactFieldsLoading: boolean;
  contactFieldsError: unknown;
  userTrii: UserTrii | null;
  userLoading: boolean;
  userError: unknown;
};

export function useEditContactModalController({
  open,
  baseUrl,
  spaceId,
  contactId,
  entityId,
  entityType,
  initialContactData,
  initialEntityData,
  contactNameFormat,
  resetOnClose = true,
}: UseEditContactModalControllerArgs) {
  const abortRef = useRef<AbortController | null>(null);

  const api = useMemo(() => {
    if (!baseUrl || !spaceId) return null;
    return createEditContactApi(baseUrl, spaceId);
  }, [baseUrl, spaceId]);

  const [state, setState] = useState<ControllerState>({
    isLoading: false,
    error: null,
    contactData: null,
    labels: [],
    labelsLoading: false,
    labelsError: null,
    contactFields: [],
    contactFieldsLoading: false,
    contactFieldsError: null,
    userTrii: null,
    userLoading: false,
    userError: null,
  });

  const resolvedEntityId = entityId ?? contactId;
  const initialData = initialEntityData ?? initialContactData;

  const canFetch = useMemo(
    () => Boolean(open && api && resolvedEntityId && !initialData),
    [open, api, resolvedEntityId, initialData],
  );

  useEffect(() => {
    if (!open || !initialData) return;
    setState((prev) => ({
      ...prev,
      isLoading: false,
      error: null,
      contactData: initialData,
    }));
  }, [open, initialData]);

  useEffect(() => {
    if (open) return;
    if (!resetOnClose) return;
    abortRef.current?.abort();
    setState({
      isLoading: false,
      error: null,
      contactData: null,
      labels: [],
      labelsLoading: false,
      labelsError: null,
      contactFields: [],
      contactFieldsLoading: false,
      contactFieldsError: null,
      userTrii: null,
      userLoading: false,
      userError: null,
    });
  }, [open, resetOnClose]);

  const canFetchLabels = useMemo(() => Boolean(open && api), [open, api]);

  useEffect(() => {
    if (!canFetchLabels || !api) {
      return;
    }

    const controller = new AbortController();
    setState((prev) => ({ ...prev, labelsLoading: true, labelsError: null }));

    api
      .fetchLabels({ signal: controller.signal })
      .then((labels) => {
        setState((prev) => ({ ...prev, labels, labelsLoading: false, labelsError: null }));
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setState((prev) => ({ ...prev, labelsLoading: false, labelsError: err }));
      });

    return () => {
      controller.abort();
    };
  }, [api, canFetchLabels]);

  useEffect(() => {
    if (!canFetch || !api) {
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setState((prev) => ({ ...prev, isLoading: true, error: null, contactData: null }));

    const request =
      entityType === 'business'
        ? api.fetchBusiness({ businessId: resolvedEntityId!, signal: controller.signal })
        : api.fetchContact({ contactId: resolvedEntityId!, signal: controller.signal });

    request
      .then((data) => {
        setState((prev) => ({ ...prev, isLoading: false, error: null, contactData: data }));
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setState((prev) => ({ ...prev, isLoading: false, error: err }));
      });

    return () => {
      controller.abort();
    };
  }, [api, canFetch, resolvedEntityId, entityType]);

  const canFetchUser = useMemo(() => Boolean(open && api), [open, api]);

  useEffect(() => {
    if (!canFetchUser || !api) return;

    const controller = new AbortController();
    setState((prev) => ({ ...prev, userLoading: true, userError: null }));

    api
      .fetchUserTrii({ signal: controller.signal })
      .then((data) => {
        setState((prev) => ({ ...prev, userLoading: false, userError: null, userTrii: data }));
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setState((prev) => ({ ...prev, userLoading: false, userError: err }));
      });

    return () => {
      controller.abort();
    };
  }, [api, canFetchUser]);

  const selectors = useMemo(() => {
    const contactData = state.contactData;
    const business = isBusiness(contactData);
    const contact = isContact(contactData);

    let contactType: 'business' | 'contact' | 'unknown' = 'unknown';
    if (business) contactType = 'business';
    if (contact) contactType = 'contact';

    let displayName = 'Unknown Contact';
    if (business) {
      displayName = contactData.name;
    } else if (contact) {
      displayName = formatContactName(
        contactData.firstName,
        contactData.lastName,
        contactNameFormat,
      );
    }

    const tags: IContactTag[] = contactData?.tags ?? [];

    return {
      isBusiness: business,
      isContact: contact,
      contactType,
      displayName,
      tags,
      labels: state.labels,
      contactFields: state.contactFields,
      userTrii: state.userTrii,
      userLoading: state.userLoading,
      userError: state.userError,
    };
  }, [
    state.contactData,
    state.labels,
    state.contactFields,
    state.userTrii,
    state.userLoading,
    state.userError,
    contactNameFormat,
  ]);

  useEffect(() => {
    if (!open) return;
    if (!api) return;
    const contactData = state.contactData;
    const business = isBusiness(contactData);
    const contact = isContact(contactData);
    const forType: 'contact' | 'business' | null = business
      ? 'business'
      : contact
        ? 'contact'
        : null;
    if (!forType) return;

    const controller = new AbortController();
    setState((prev) => ({ ...prev, contactFieldsLoading: true, contactFieldsError: null }));

    api
      .fetchContactFields({ forType, signal: controller.signal })
      .then((fields) => {
        setState((prev) => ({
          ...prev,
          contactFields: fields,
          contactFieldsLoading: false,
          contactFieldsError: null,
        }));
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setState((prev) => ({ ...prev, contactFieldsLoading: false, contactFieldsError: err }));
      });

    return () => {
      controller.abort();
    };
  }, [api, open, state.contactData]);

  return {
    state,
    selectors,
    actions: {
      setContactData: (contactData?: IContact | IBusiness) =>
        setState((prev) => ({ ...prev, contactData: contactData ?? null })),
      clearError: () => setState((prev) => ({ ...prev, error: null })),
    },
  };
}
